import React from 'react';
import {
  Container,
  Header,
  SpaceBetween,
  Tabs,
  Table,
  Box,
  ColumnLayout,
  ExpandableSection,
  StatusIndicator,
  Button
} from '@cloudscape-design/components';

const DomainDetails = ({ domain, onDismiss }) => {
  return (
    <Container
      header={
        <Header
          variant="h2"
          actions={
            <Button onClick={onDismiss}>Close</Button>
          }
        >
          {domain.name} Details
        </Header>
      }
    >
      <Tabs
        tabs={[
          {
            label: "Overview",
            id: "overview",
            content: (
              <SpaceBetween size="l">
                <ColumnLayout columns={2}>
                  <Box>
                    <Box variant="awsui-key-label">Bounded Context</Box>
                    <Box>{domain.context.bounded_context}</Box>
                  </Box>
                  <Box>
                    <Box variant="awsui-key-label">Business Capability</Box>
                    <Box>{domain.context.business_capability}</Box>
                  </Box>
                  <Box>
                    <Box variant="awsui-key-label">Domain Type</Box>
                    <StatusIndicator type={
                      domain.context.domain_type === 'CORE' ? 'success' :
                      domain.context.domain_type === 'SUPPORTING' ? 'info' : 'warning'
                    }>
                      {domain.context.domain_type}
                    </StatusIndicator>
                  </Box>
                </ColumnLayout>
                <ExpandableSection header="Domain Scope">
                  <ul>
                    {domain.context.domain_scope.map((scope, i) => (
                      <li key={i}>{scope}</li>
                    ))}
                  </ul>
                </ExpandableSection>
                <Box variant="awsui-key-label">Synthesis Justification</Box>
                <Box>{domain.context.synthesis_justification}</Box>
              </SpaceBetween>
            )
          },
          {
            label: "Domain Model",
            id: "model",
            content: (
              <SpaceBetween size="l">
                {domain.domain_model.entities.map((entity, index) => (
                  <ExpandableSection
                    key={index}
                    header={
                      <Header variant="h3">
                        {entity.name} ({entity.role})
                      </Header>
                    }
                  >
                    <Table
                      columnDefinitions={[
                        { id: 'name', header: 'Attribute', cell: item => item.name },
                        { id: 'type', header: 'Type', cell: item => item.type },
                        { id: 'constraints', header: 'Constraints', cell: item => item.constraints.join(', ') },
                        { id: 'privacy', header: 'Privacy Level', cell: item => item.privacy_level }
                      ]}
                      items={entity.attributes}
                      variant="embedded"
                    />
                  </ExpandableSection>
                ))}
              </SpaceBetween>
            )
          },
          {
            label: "Business Operations",
            id: "operations",
            content: (
              <Table
                columnDefinitions={[
                  { id: 'name', header: 'Operation', cell: item => item.name },
                  { id: 'type', header: 'Type', cell: item => item.type },
                  { id: 'entities', header: 'Entities Involved', cell: item => item.entities_involved.join(', ') },
                  { id: 'consistency', header: 'Consistency', cell: item => item.consistency_requirement }
                ]}
                items={domain.business_operations}
                variant="embedded"
              />
            )
          },
          {
            label: "Service Contracts",
            id: "contracts",
            content: (
              <Tabs
                tabs={[
                  {
                    label: "APIs",
                    content: (
                      <Table
                        columnDefinitions={[
                          { id: 'name', header: 'Name', cell: item => item.name },
                          { id: 'type', header: 'Type', cell: item => item.type },
                          { id: 'path', header: 'Path', cell: item => item.path },
                          { id: 'consistency', header: 'Consistency', cell: item => item.consistency_level }
                        ]}
                        items={domain.service_contracts.apis}
                        variant="embedded"
                      />
                    )
                  },
                  {
                    label: "Events",
                    content: (
                      <Table
                        columnDefinitions={[
                          { id: 'name', header: 'Name', cell: item => item.name },
                          { id: 'type', header: 'Type', cell: item => item.type },
                          { id: 'consistency', header: 'Consistency', cell: item => item.consistency_guarantee }
                        ]}
                        items={domain.service_contracts.events}
                        variant="embedded"
                      />
                    )
                  }
                ]}
              />
            )
          },
          {
            label: "Implementation Guidance",
            id: "implementation",
            content: (
              <SpaceBetween size="l">
                <ColumnLayout columns={2}>
                  <Box>
                    <Box variant="awsui-key-label">Architectural Style</Box>
                    <Box>{domain.implementation_guidance.architectural_style}</Box>
                  </Box>
                  <Box>
                    <Box variant="awsui-key-label">Persistence</Box>
                    <Box>{domain.implementation_guidance.persistence.type}</Box>
                  </Box>
                </ColumnLayout>
                <ExpandableSection header="Deployment">
                  <SpaceBetween size="s">
                    <Box>Scaling Requirements: {domain.implementation_guidance.deployment.scaling_requirements}</Box>
                    <Box>Resource Requirements: {domain.implementation_guidance.deployment.resource_requirements}</Box>
                    <Box>Availability Requirements: {domain.implementation_guidance.deployment.availability_requirements}</Box>
                  </SpaceBetween>
                </ExpandableSection>
                <ExpandableSection header="Monitoring">
                  <SpaceBetween size="s">
                    <Box variant="strong">Key Metrics</Box>
                    <ul>
                      {domain.implementation_guidance.monitoring.key_metrics.map((metric, i) => (
                        <li key={i}>{metric}</li>
                      ))}
                    </ul>
                    <Box variant="strong">SLAs</Box>
                    <ul>
                      {Object.entries(domain.implementation_guidance.monitoring.slas).map(([key, value], i) => (
                        <li key={i}>{key}: {value}</li>
                      ))}
                    </ul>
                  </SpaceBetween>
                </ExpandableSection>
              </SpaceBetween>
            )
          }
        ]}
      />
    </Container>
  );
};

export default DomainDetails;
